import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { WithId } from '@common/typescript/objects/WithId';
import { Nullable, Optional } from '@common/typescript/objects/Nullable';

import { DeliveryTypeEntity } from '@app/objects/DeliveryType';
import { useCrematoryFeatures } from '@app/hooks/Crematory/useCrematoryFeatures';
import { ApplicationState } from '@app/store';
import { CrematoryFeatures } from '@app/objects/CrematoryFeatures';
import { DeliveryTypeRecord, getActionCreators } from '@app/store/DeliveryTypes';

interface DeliveryTypes {
	map: Map<number, DeliveryTypeEntity>;
	list: Array<DeliveryTypeEntity>;
	error: Nullable<string>;
	loading: boolean;

	update: (item: Partial<DeliveryTypeEntity> & WithId) => void;
	delete: (id: number) => void;
	create: (item: DeliveryTypeEntity) => void;
	reload: (force?: boolean) => void;
}

function selector(state: ApplicationState, features: Optional<CrematoryFeatures>): DeliveryTypeRecord | undefined {
	if (!features?.enableCustomDeliveryType) return undefined;

	return state.deliveryTypes[features.crematoryId];
}

function assert(name: string) {
	return () => { throw new Error(`Function ${name} not implemented`); };
}

const defaultItem: DeliveryTypes = {
	map: new Map<number, DeliveryTypeEntity>(),
	list: [],
	error: null,
	loading: false,

	update: assert('DeliveryTypes.update'),
	delete: assert('DeliveryTypes.delete'),
	create: assert('DeliveryTypes.create'),
	reload: assert('DeliveryTypes.reload'),
};

export function useDeliveryTypes(): DeliveryTypes {
	const features = useCrematoryFeatures();
	const dispatch = useDispatch();
	const store = useSelector((state: ApplicationState) => selector(state, features.item));

	React.useEffect(() => {
		if (features.isLoading) return;
		if (!features.item?.crematoryId) return;

		const actions = getActionCreators(features.item.crematoryId);
		dispatch(actions.request());
	}, [features.isLoading, features.item?.crematoryId, dispatch]);

	return React.useMemo(() => {
		if (!features.item) return defaultItem;

		const crematoryId = features.item.crematoryId;
		const factory = getActionCreators(crematoryId);

		return {
			map: store?.map ?? new Map<number, DeliveryTypeEntity>(),
			list: store?.map ? Array.from(store.map.values()) : [],
			error: store?.error ?? null,
			loading: Boolean(store && store.state !== 'idle'),

			update: (item: Partial<DeliveryTypeEntity> & WithId) => dispatch(factory.mutate({ item, crematoryId })),
			delete: (id: number) => dispatch(factory.mutate({ item: { id, deleted: true }, crematoryId })),
			create: (item: DeliveryTypeEntity) => dispatch(factory.mutate({ item, crematoryId })),
			reload: (force?: boolean) => dispatch(factory.request(force)),
		};
	}, [features.item?.crematoryId, store?.map, store?.error, store?.state, dispatch]);
}
